const narrative = (id, title, topic, guidance = "") => ({
	id, title, topic, guidance, type: "MULTILINE", isRequired: false,
})

const fields = [
	narrative("concern", "How can I be useful to you as you construct your career?", "Presenting concern"),
	narrative("roleModels", "Who did you admire when you were growing up? Tell me about her or him.", "Role models", "Seek three non-parent role models admired at about ages six to nine; ask what characteristics the client admires."),
	narrative("media", "Do you read any magazines or watch any television shows regularly? Which ones?\n\nWhat do you like about these magazines or television shows?", "Preferred settings", "Websites may also reveal what attracts the client."),
	narrative("story", "What is your favorite book or movie? Tell me the story.", "Favorite story", "Invite the client to tell a current favorite story in their own words."),
	narrative("motto", "Tell me your favorite saying or motto.", "Motto", "If none comes to mind, invite a frequently considered saying or a saying the client makes up."),
	...Array.from({length: 3}, (_, index) => {
		const number = index + 1
		return [
				narrative(`recollection${number}`, `${number === 1 ? "What are your earliest recollections? I am interested in hearing three stories about things you recall happening to you when you were three to six years.\n\n" : ""}Early recollection ${number}`, "Early recollections"),
			narrative(`recollection${number}Feeling`, "How did you feel?", "Early recollections"),
			narrative(`recollection${number}Image`, "What is the most vivid image?", "Early recollections"),
			narrative(`recollection${number}Headline`, "What headline would you give this story?", "Early recollections", "Compose the headline with the client; keep their wording."),
		]
	}).flat(),
]

export function createCareerConstructionInterviewConfig() {
	return {
		title: "Career Construction Interview (CCI)",
		instructions: "Tell your stories in your own words. You can pause and return, and any question may be left unanswered.",
		sequence: "adultCciCounselorWorksheet",
		fixedPromptSequence: true,
		fields: JSON.parse(JSON.stringify(fields)),
		portraitSections: [
			{id: "perspective", title: "Presenting concern and perspective"},
			{id: "self", title: "Self"},
			{id: "setting", title: "Setting"},
			{id: "script", title: "Script"},
			{id: "successFormula", title: "Success formula"},
			{id: "selfAdvice", title: "Self-advice"},
		],
		reviewLabels: {
			themes: "Recurring themes",
			formulation: "Practitioner formulation",
			concernConnection: "How themes relate to the presenting concern",
			summary: "Life-career portrait and formulation",
			dialogue: "Dialogue and next steps",
		},
	}
}

export function createCareerConstructionInterviewDemoPayload() {
	return {
		form: createCareerConstructionInterviewConfig(),
		answers: {
			concern: "I am unsure whether to leave my current job for work with more room to create.",
			roleModels: "I admired an aunt who made things and helped neighbors learn.",
			media: "I often visit design websites. I like seeing people turn a rough idea into something useful.",
			motto: "Make room for what matters.",
			story: "I like stories where someone brings a group together to solve a practical problem. The part I remember is when their different ideas finally fit together.",
			recollection1: "I made a cardboard house with my aunt. She let me decide where the windows should go, and we kept trying until it stood up.",
			recollection1Feeling: "Proud and absorbed in what we were doing.",
			recollection1Image: "Holding the roof while the glue dried.",
			recollection1Headline: "Making something together.",
			recollection2: "I showed a younger neighbor how to fold a paper boat. We tried several times before it floated.",
			recollection2Feeling: "Patient and pleased when it worked.",
			recollection2Image: "The boat floating across a puddle.",
			recollection2Headline: "Helping someone find their own way.",
			recollection3: "I rearranged a small corner of our room to make a place for drawing. I liked having somewhere that was mine.",
			recollection3Feeling: "Calm and excited to start.",
			recollection3Image: "My pencils lined up beside a blank page.",
			recollection3Headline: "Making room to create.",
		},
		privatePractitioner: {
			notes: {concern: "Explore opportunities to create within the current role before assuming a change of employer is necessary."},
			portrait: {
				perspective: "The client wants more creative involvement while retaining the stability of their current work.",
				self: "The client describes being a practical maker who enjoys helping others learn.",
				setting: "Collaborative environments with space to test ideas appear attractive.",
				script: "An initial difficulty becomes an opportunity to try, adapt, and make something useful with others.",
				successFormula: "Bring people and ideas together to build something useful, with enough freedom to experiment.",
				selfAdvice: "Make room for creative work now, rather than waiting for a perfect job to provide it.",
			},
			sectionSources: {perspective: ["concern"], self: ["roleModels", "recollection2"], setting: ["media", "story"], script: ["recollection1", "recollection2"], successFormula: ["story", "media"], selfAdvice: ["motto", "recollection3"]},
			concernConnection: "The wish to leave may reflect a need for creative participation more than a wish to abandon the current field. Discuss whether that reading fits the client's experience.",
			summary: "Across these stories, making useful things with other people is a recurring source of satisfaction. The next career move could be assessed by how much room it offers for that activity.",
			dialogue: "The client will ask about joining a small design project at work and speak with someone in a more creative role. At the next meeting, compare those experiences with the qualities identified here.",
			themes: [
				{id: "theme_1", text: "Creating useful things", sourceIds: ["media", "roleModels"], confirmed: true},
				{id: "theme_2", text: "Making room for what matters", sourceIds: ["motto"], confirmed: true},
				{id: "theme_3", text: "Learning from people who create", sourceIds: ["roleModels", "media"], confirmed: true},
			],
		},
	}
}
